import { Card, Checkbox, LearnMoreLink, Text } from '@ui/themed';
import { t } from 'i18next';
import { useEffect } from 'react';
import {
  featureRestricted,
  features,
  getActiveBillingPlanKey,
} from '../../../../constants/features';
import { useEnv } from '../../../../context/env-context';
import { useSettings } from '../../../../context/settings-context';
import { SETTING_KEY_TO_LABEL } from '../../constants';
import { useCustomerGroupBuilderForm } from '../../hooks/use-customer-group-builder-form';
import { QuotingUnavailableAlert } from './quoting-unavailable-alert';
import { ResetSettingsButton } from './reset-settings-button';

export function QuotingEngineCard() {
  const form = useCustomerGroupBuilderForm();
  const settings = useSettings();
  const env = useEnv();

  const isQuotingEngineRestricted = featureRestricted(
    features.QUOTING,
    getActiveBillingPlanKey(env.billing.plan_key),
  );

  const isQuotingEngineEnabled = settings.quotingEnabled;
  const isQuotingUnavailable = !isQuotingEngineEnabled || isQuotingEngineRestricted;

  // useEffect to watch if feature has been turned on, for fast response
  useEffect(() => {
    form.setFieldValue(
      'settings.quotingEngine.disabled',
      isQuotingUnavailable || !isQuotingEngineEnabled || undefined,
    );
  }, [isQuotingUnavailable, isQuotingEngineEnabled]);

  return (
    <Card showBorder>
      <Card.Header collapsible headingLevel="h2" icon="calculator">
        {SETTING_KEY_TO_LABEL.quotingEngine}
      </Card.Header>
      <Card.Body>
        <Text variant="secondary">
          {t('features:customer_group.components.quoting_engine_card.description')}{' '}
          <LearnMoreLink link="https://docs.sparklayer.io/quoting" />
        </Text>
        {isQuotingUnavailable ? (
          <QuotingUnavailableAlert
            isRestricted={isQuotingEngineRestricted}
            title={t(
              `labels:${isQuotingEngineRestricted ? 'feature_restricted' : 'feature_not_enabled'}`,
            )}
            description={t(
              `features:customer_group.components.quoting_engine_card.errors.${isQuotingEngineRestricted ? 'restricted' : 'not_enabled'}`,
            )}
          />
        ) : (
          <form.Field name="settings.quotingEngine.customer_create_quote">
            {(field) => (
              <Checkbox
                key="quote"
                label={t(
                  'features:customer_group.components.quoting_engine_card.allow_submission.label',
                )}
                checked={field.state.value ?? false}
                description={t(
                  'features:customer_group.components.quoting_engine_card.allow_submission.description',
                )}
                onChange={() => field.handleChange((prev) => !prev)}
              />
            )}
          </form.Field>
        )}
        <div className="flex justify-start">
          <form.Field name="settings.quotingEngine">
            {(field) => <ResetSettingsButton field={field} />}
          </form.Field>
        </div>
      </Card.Body>
    </Card>
  );
}
